import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import swal from "sweetalert";
import Container from "../../components/Container/Container";
import { API, ROUTER } from "../../constants/env";
import {
  formatProfilName,
  formatFirstName,
  formatLastName,
} from "../../utils/displayName";

const TRI_SHORT = {
  premier: "1er trim.",
  deuxieme: "2ème trim.",
  troisieme: "3ème trim.",
};
const TRI_ORDER = { premier: 1, deuxieme: 2, troisieme: 3 };
const PAGE_SIZE = 10;

const ListCarnet = () => {
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [triFilter, setTriFilter] = useState("all");
  const [sortBy, setSortBy] = useState("lastName");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    localStorage.removeItem("tris");
    setLoading(true);
    // On charge tous les carnets des classes du prof en une fois (volume faible,
    // liste déjà scopée serveur), puis filtres + pagination côté client.
    fetch(`${API.carnetStudent.list}?page=1&count=2000&searchQuery=`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": localStorage.getItem("token"),
      },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.error) {
          return swal("Erreur", response.message, "error");
        }
        const mapped = (response.data || []).map((elem) => ({
          key: elem._id,
          name: elem.student ? formatProfilName(elem.student.profil) : "",
          firstName:
            elem.student && elem.student.profil
              ? formatFirstName(elem.student.profil.firstName)
              : "",
          lastName:
            elem.student && elem.student.profil
              ? formatLastName(elem.student.profil.lastName)
              : "",
          classeId:
            elem.student && elem.student.classe ? elem.student.classe._id : "",
          classeName:
            elem.student && elem.student.classe ? elem.student.classe.name : "",
          trimestres: (elem.reportCards || [])
            .filter((rc) => rc && rc.semestre)
            .sort(
              (a, b) =>
                (TRI_ORDER[a.semestre] || 9) - (TRI_ORDER[b.semestre] || 9)
            )
            .map((rc) => ({ id: rc._id, semestre: rc.semestre })),
        }));
        setRows(mapped);
      })
      .catch((e) => swal("Erreur", e.message, "error"))
      .finally(() => setLoading(false));
  }, []);

  // Classes du prof = classes présentes dans ses carnets (scope exact, pas tout l'école)
  const classOptions = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (r.classeId && !m.has(r.classeId)) m.set(r.classeId, r.classeName);
    });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [rows]);

  const visibleTrimestres = (row) =>
    triFilter === "all"
      ? row.trimestres
      : row.trimestres.filter((t) => t.semestre === triFilter);

  const filtered = useMemo(() => {
    // Recherche multi-mots : chaque mot doit matcher prénom ou nom (ordre libre).
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
      if (classFilter !== "all" && r.classeId !== classFilter) return false;
      if (words.length) {
        const hay = (r.firstName + " " + r.lastName).toLowerCase();
        if (!words.every((w) => hay.includes(w))) return false;
      }
      if (visibleTrimestres(r).length === 0) return false;
      return true;
    });
  }, [rows, search, classFilter, triFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    return [...filtered].sort(
      (a, b) =>
        (a[sortBy] || "").localeCompare(b[sortBy] || "", "fr", {
          sensitivity: "base",
        }) * dir
    );
  }, [filtered, sortBy, sortDir]);

  useEffect(() => {
    setPage(1);
  }, [search, classFilter, triFilter, sortBy, sortDir]);

  const totalPage = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCarnet = (rc) => {
    localStorage.setItem("tris", rc.semestre);
    history.push(ROUTER.CARNETS.EDIT(rc.id));
  };

  return (
    <Container name="Carnets de notes">
      <div className="card card-custom gutter-b">
        <div className="card-header">
          <div className="card-title">
            <h3 className="card-label">Carnets de mes classes</h3>
          </div>
        </div>
        <div className="card-body">
          <div className="form-group row">
            <div className="col-lg-3">
              <input
                type="text"
                className="form-control"
                placeholder="Recherche élève"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-lg-3">
              <select
                className="form-control"
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <option value="all">Toutes les classes</option>
                {classOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-3">
              <select
                className="form-control"
                value={triFilter}
                onChange={(e) => setTriFilter(e.target.value)}
              >
                <option value="all">Tous les trimestres</option>
                <option value="premier">1er trimestre</option>
                <option value="deuxieme">2ème trimestre</option>
                <option value="troisieme">3ème trimestre</option>
              </select>
            </div>
            <div className="col-lg-3 mt-2 mt-lg-0">
              <select
                className="form-control"
                value={`${sortBy}_${sortDir}`}
                onChange={(e) => {
                  const [by, dir] = e.target.value.split("_");
                  setSortBy(by);
                  setSortDir(dir);
                }}
              >
                <option value="lastName_asc">Nom A → Z</option>
                <option value="lastName_desc">Nom Z → A</option>
                <option value="firstName_asc">Prénom A → Z</option>
                <option value="firstName_desc">Prénom Z → A</option>
              </select>
            </div>
          </div>

          <div className="react-bootstrap-table table-responsive">
            <table className="table table-head-custom table-vertical-center overflow-hidden">
              <thead>
                <tr>
                  <th>Élève</th>
                  <th>Classe</th>
                  <th className="text-right pr-3">Carnets</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted">
                      Chargement…
                    </td>
                  </tr>
                )}
                {!loading && pageRows.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted">
                      Aucun carnet
                    </td>
                  </tr>
                )}
                {!loading &&
                  pageRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td>{row.classeName}</td>
                      <td className="text-right pr-0">
                        {visibleTrimestres(row).map((rc) => (
                          <button
                            key={rc.id}
                            type="button"
                            className="btn btn-sm btn-light-primary font-weight-bold mx-1"
                            onClick={() => openCarnet(rc)}
                          >
                            {TRI_SHORT[rc.semestre] || rc.semestre}
                          </button>
                        ))}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center flex-wrap py-3">
            <span className="text-muted">
              {sorted.length} élève(s) · page {page} / {totalPage}
            </span>
            <div>
              <button
                type="button"
                className="btn btn-sm btn-light mr-2"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ◀ Précédent
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={page >= totalPage}
                onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
              >
                Suivant ▶
              </button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default ListCarnet;
